import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { ensureAuthSession, getAuthRouteAccess } from '@/modules/auth/public'
import { ensureFeatureFlagsReady } from '@/modules/common/public'
import { ensureSupplierProfileLoaded, getSupplierRouteAccess } from '@/modules/supplier/public'

export async function runAppNavigationGuards(
  to: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> {
  const authStore = await ensureAuthSession({
    requiresGuest: Boolean(to.meta['requiresGuest']),
    requiresSupplierGuest: Boolean(to.meta['requiresSupplierGuest']),
  })
  const auth = getAuthRouteAccess(authStore)
  const supplier = getSupplierRouteAccess()

  if (to.meta['requiresSupplierAuth'] && !supplier.isAuthenticated) {
    if (!auth.isAuthenticated) {
      next({ name: 'login', query: { redirect: to.fullPath } })
    } else {
      next({ name: 'home' })
    }
    return
  }

  if (to.meta['requiresSupplierAuth'] && supplier.isAuthenticated) {
    await ensureSupplierProfileLoaded()
  }

  if (to.meta['requiresSupplierGuest'] && supplier.isAuthenticated) {
    next({ name: 'supplier-dashboard' })
    return
  }

  if (to.meta['requiresAuth'] && !auth.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } })
    return
  }

  if (to.meta['requiresGuest'] && auth.isAuthenticated) {
    next({ name: 'home' })
    return
  }

  if (to.meta['requiresAdmin'] && !auth.isAdmin) {
    next({ name: 'home' })
    return
  }

  if (to.meta['blockSupplier'] && auth.isSupplier) {
    next({ name: 'supplier-dashboard' })
    return
  }

  if (to.meta['requiresFeature']) {
    const featureFlags = await ensureFeatureFlagsReady()
    const feature = to.meta['requiresFeature'] as string
    if (!featureFlags.isEnabled(feature)) {
      next({ name: 'not-found' })
      return
    }
  }

  next()
}
